import { Skeleton } from "@/components/ui/skeleton";

/* =========================================================================
   Pre-composed skeletons matching actual page layouts.
   Goal: the skeleton should reserve the same space as the real content so
   there's zero CLS (cumulative layout shift) when real data arrives.

   Note on `key`s:
   These render a *fixed-length, never-mutated* range of placeholders that
   exists only until the real data arrives. Index keys are the correct
   idiom here — there are no stable IDs to use, and items never reorder.
   We use `slot-{i}` to make this intent obvious to lint + readers.
   ========================================================================= */

function range(n, prefix) {
  return Array.from({ length: n }, (_, i) => `${prefix}-${i}`);
}

export function MenuItemTileSkeleton() {
  return (
    <div className="bp-card p-3 sm:p-4" data-testid="skel-menu-tile">
      <Skeleton className="aspect-[4/5] rounded-xl w-full" />
      <div className="mt-3 space-y-2">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-3/4" />
        <div className="flex items-center justify-between mt-3">
          <Skeleton className="h-5 w-14" />
          <Skeleton className="h-8 w-20 rounded-full" />
        </div>
      </div>
    </div>
  );
}

export function MenuGridSkeleton({ count = 8 }) {
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
      data-testid="skel-menu-grid"
    >
      {range(count, "menu-tile").map((k) => (
        <MenuItemTileSkeleton key={k} />
      ))}
    </div>
  );
}

export function DrinkTileSkeleton() {
  return (
    <div className="bp-tile" data-testid="skel-drink-tile">
      <Skeleton className="aspect-[4/5] rounded-xl w-full" />
    </div>
  );
}

export function SignatureDrinksSkeleton() {
  return (
    <div
      className="mt-7 grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4"
      data-testid="skel-signature-drinks"
    >
      <div className="md:col-span-2 md:row-span-2">
        <Skeleton className="aspect-[4/3] md:aspect-auto md:min-h-[320px] w-full rounded-xl" />
      </div>
      {range(5, "sig-tile").map((k) => (
        <DrinkTileSkeleton key={k} />
      ))}
    </div>
  );
}

export function TableSkeleton({ rows = 6, cols = 5 }) {
  return (
    <div className="bp-card overflow-hidden" data-testid="skel-table">
      <div className="bg-muted/40 border-b px-4 py-3 flex gap-3">
        {range(cols, "th").map((k) => (
          <Skeleton key={k} className="h-4 flex-1" />
        ))}
      </div>
      {range(rows, "row").map((k) => (
        <div key={k} className="px-4 py-3 flex items-center gap-3 border-b last:border-b-0">
          <Skeleton className="h-9 w-9 rounded-full shrink-0" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-1/2" />
            <Skeleton className="h-3 w-1/3" />
          </div>
          <Skeleton className="h-8 w-20" />
        </div>
      ))}
    </div>
  );
}

export function StatsRowSkeleton({ cards = 4 }) {
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4"
      data-testid="skel-stats-row"
    >
      {range(cards, "stat").map((k) => (
        <div key={k} className="bp-card p-4 sm:p-5">
          <Skeleton className="h-3 w-24 mb-3" />
          <Skeleton className="h-7 w-20 mb-2" />
          <Skeleton className="h-3 w-16" />
        </div>
      ))}
    </div>
  );
}

export function FormSkeleton({ fields = 4 }) {
  return (
    <div className="bp-card p-5 sm:p-6 space-y-4" data-testid="skel-form">
      {range(fields, "field").map((k) => (
        <div key={k}>
          <Skeleton className="h-3 w-24 mb-2" />
          <Skeleton className="h-10 w-full rounded-xl" />
        </div>
      ))}
    </div>
  );
}

export function DashboardSkeleton() {
  return (
    <div className="space-y-6" data-testid="skel-dashboard">
      <div>
        <Skeleton className="h-3 w-32 mb-2" />
        <Skeleton className="h-9 w-60" />
      </div>
      <StatsRowSkeleton />
      <TableSkeleton rows={4} cols={4} />
    </div>
  );
}
